import { onBeforeUnmount, ref, watch } from 'vue';
import { api } from '@/utils/api';
import { useShiftStore } from '@/stores/shift';
import { useAuthStore } from '@/stores/auth';

/**
 * Composable de tracking GPS durante el shift.
 *
 * - Requiere consentimiento (auth.employee.gps_consent_at).
 * - Pide permiso al usuario al iniciar.
 * - Trackea con `watchPosition` (resolución alta cuando hay movimiento).
 * - Buffer en memoria + flush cada 30s al endpoint /v1/gps/batch.
 * - Si el usuario sale del warehouse bbox, el server descarta el punto (privacy).
 * - Detiene cuando se cierra el shift o se desmonta el componente.
 */

const FLUSH_INTERVAL_MS = 30_000;
const MIN_DISTANCE_M = 1;             // no guardamos puntos a < 1m del anterior

export interface GpsPointLocal {
  client_id: string;
  shift_id: string;
  latitude: number;
  longitude: number;
  accuracy_m: number;
  altitude_m?: number;
  heading_deg?: number;
  speed_mps?: number;
  recorded_at: string;
}

export function useGpsTracking() {
  const shift = useShiftStore();
  const auth = useAuthStore();

  const tracking = ref(false);
  const lastError = ref<string | null>(null);
  const pointsBuffered = ref(0);

  let watchId: number | null = null;
  let flushTimer: ReturnType<typeof setInterval> | null = null;
  let buffer: GpsPointLocal[] = [];
  let lastPoint: { lat: number; lng: number } | null = null;

  async function start(): Promise<void> {
    if (tracking.value) return;
    // 'rechazado' = el operario dijo que no. Queda registrado para no volver a
    // preguntarle, pero no se trackea nada.
    if (auth.employee?.gps_consent_version === 'rechazado') return;
    if (!auth.employee?.gps_consent_at) {
      lastError.value = 'Falta consentimiento de GPS';
      return;
    }
    if (!shift.current) {
      lastError.value = 'No hay shift abierto';
      return;
    }
    if (!('geolocation' in navigator)) {
      lastError.value = 'Este dispositivo no tiene GPS disponible';
      return;
    }

    try {
      watchId = navigator.geolocation.watchPosition(
        (pos) => onPoint(pos),
        (err) => { lastError.value = err.message; },
        {
          enableHighAccuracy: true,
          maximumAge: 10_000,
          timeout: 30_000,
        },
      );
      flushTimer = setInterval(() => void flush(), FLUSH_INTERVAL_MS);
      tracking.value = true;
      lastError.value = null;
    } catch (e) {
      lastError.value = (e as Error).message;
    }
  }

  function stop(): void {
    if (watchId !== null) {
      navigator.geolocation.clearWatch(watchId);
      watchId = null;
    }
    if (flushTimer) {
      clearInterval(flushTimer);
      flushTimer = null;
    }
    void flush();
    tracking.value = false;
  }

  function onPoint(pos: GeolocationPosition): void {
    if (!shift.current) return;
    const { latitude, longitude, accuracy, altitude, heading, speed } = pos.coords;

    if (lastPoint) {
      const d = haversineMeters(lastPoint, { lat: latitude, lng: longitude });
      if (d < MIN_DISTANCE_M) return;
    }
    lastPoint = { lat: latitude, lng: longitude };

    buffer.push({
      client_id: crypto.randomUUID(),
      shift_id: shift.current.id,
      latitude,
      longitude,
      accuracy_m: accuracy,
      altitude_m: altitude ?? undefined,
      heading_deg: heading ?? undefined,
      speed_mps: speed ?? undefined,
      recorded_at: new Date(pos.timestamp).toISOString(),
    });
    pointsBuffered.value = buffer.length;

    // Si el buffer crece mucho (offline largo), tope para no comer RAM:
    if (buffer.length > 1000) {
      buffer = buffer.slice(-500);
    }
  }

  async function flush(): Promise<void> {
    if (buffer.length === 0) return;
    if (!navigator.onLine) return;
    const batch = buffer.splice(0, 200);
    try {
      await api.post('/v1/gps/batch', { points: batch });
      pointsBuffered.value = buffer.length;
    } catch {
      // re-pusheamos al frente para reintentar
      buffer.unshift(...batch);
      pointsBuffered.value = buffer.length;
    }
  }

  // Re-evalúa cuando cambia el shift (abre / cierra)
  watch(
    () => shift.current?.id,
    (id, prev) => {
      if (id && !prev && !tracking.value) void start();
      if (!id && prev && tracking.value) stop();
    },
  );

  onBeforeUnmount(() => stop());

  return { tracking, lastError, pointsBuffered, start, stop, flush };
}

function haversineMeters(
  a: { lat: number; lng: number },
  b: { lat: number; lng: number },
): number {
  const R = 6_371_000;
  const toRad = (d: number) => (d * Math.PI) / 180;
  const dLat = toRad(b.lat - a.lat);
  const dLng = toRad(b.lng - a.lng);
  const h =
    Math.sin(dLat / 2) ** 2 +
    Math.cos(toRad(a.lat)) * Math.cos(toRad(b.lat)) * Math.sin(dLng / 2) ** 2;
  return 2 * R * Math.asin(Math.sqrt(h));
}
